import React, { useEffect, useRef, useState } from 'react';

interface TurnstileApi {
    render: (
        container: HTMLElement,
        options: {
            sitekey: string;
            callback: (token: string) => void;
            'expired-callback': () => void;
            'error-callback': () => void;
        },
    ) => string;
    remove: (widgetId: string) => void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let turnstileScript: Promise<void> | null = null;

const loadTurnstile = (): Promise<void> => {
    if (window.turnstile) return Promise.resolve();
    if (turnstileScript) return turnstileScript;

    turnstileScript = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => window.turnstile ? resolve() : reject(new Error('Turnstile không khả dụng.'));
        script.onerror = () => reject(new Error('Không tải được CAPTCHA.'));
        document.head.appendChild(script);
    }).catch((error) => {
        turnstileScript = null;
        throw error;
    });

    return turnstileScript;
};

interface TurnstileWidgetProps {
    onVerify: (token: string) => void;
}

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ onVerify }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    useEffect(() => {
        let mounted = true;
        let widgetId: string | undefined;

        if (!siteKey) {
            setError('CAPTCHA chưa được cấu hình.');
            onVerify('');
            return () => {
                mounted = false;
            };
        }

        loadTurnstile()
            .then(() => {
                if (!mounted || !containerRef.current || !window.turnstile) return;
                widgetId = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    callback: (token) => onVerify(token),
                    'expired-callback': () => onVerify(''),
                    'error-callback': () => {
                        onVerify('');
                        setError('Không thể xác minh CAPTCHA. Hãy tải lại thử thách.');
                    },
                });
            })
            .catch(() => {
                if (mounted) setError('Không tải được CAPTCHA. Kiểm tra kết nối rồi thử lại.');
            });

        return () => {
            mounted = false;
            if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
        };
    }, [onVerify, siteKey]);

    return (
        <div>
            <div ref={containerRef} />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default TurnstileWidget;